import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { RotateCw, ChevronDown, ChevronUp } from 'lucide-react';
import { 
  BankSearch, 
  BankSearchPool, 
  BankSearchWeights, 
  ResultsOverride 
} from '../../../../utils/types/candi_bank_types';
import { SkillChipRow } from './SkillChipRow';
import { WeightSliders } from './WeightSliders';

interface BankSearchEditorProps {
  search: BankSearch;
  pool: BankSearchPool | null;
  isReindexing: boolean;
  onPatchSkills: (required: string[], mandatory: string[], desirable: string[]) => Promise<void>;
  onPatchJobTitle: (jobTitle: string) => Promise<void>;
  onPatchEducations: (educations: string[]) => Promise<void>;
  onPatchCertifications: (certifications: string[]) => Promise<void>;
  onPatchRemoveMatches: (skill: string, matches: string[]) => Promise<void>;
  onPatchExperience: (min: number, require: boolean) => Promise<void>;
  onPatchTitle: (title: string) => Promise<void>;
  onRefreshFromOpening: () => Promise<void>;
  weightOverride: Partial<BankSearchWeights> | null;
  onWeightOverrideChange: (override: Partial<BankSearchWeights> | null) => void;
  onCommitWeights: () => Promise<void>;
  onResetWeights: () => void;
  onApplyWeightOverride: (override: ResultsOverride) => void;
}

export const BankSearchEditor: React.FC<BankSearchEditorProps> = ({
  search,
  pool,
  isReindexing,
  onPatchSkills,
  onPatchJobTitle,
  onPatchRemoveMatches,
  onPatchExperience,
  onPatchTitle,
  onRefreshFromOpening,
  weightOverride,
  onWeightOverrideChange,
  onCommitWeights,
  onResetWeights,
  onApplyWeightOverride,
}) => {
  const [title, setTitle] = useState(search.title || '');
  const [jobTitle, setJobTitle] = useState(search.jobTitle || '');
  const [isCriteriaExpanded, setIsCriteriaExpanded] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    setTitle(search.title || '');
  }, [search.title]);

  useEffect(() => {
    setJobTitle(search.jobTitle || '');
  }, [search.jobTitle]);

  const handleTitleBlur = () => {
    if (title !== (search.title || '') && title.trim() !== '') {
      onPatchTitle(title.trim());
    }
  };

  const handleJobTitleBlur = () => {
    if (jobTitle !== (search.jobTitle || '')) {
      onPatchJobTitle(jobTitle.trim());
    }
  };

  const handleSkillsChange = (tier: 'required' | 'mandatory' | 'desirable', newSkills: string[]) => {
    const r = tier === 'required' ? newSkills : search.requiredSkills;
    const m = tier === 'mandatory' ? newSkills : search.mandatorySkills;
    const d = tier === 'desirable' ? newSkills : search.desirableSkills;
    onPatchSkills(r, m, d);
  };

  const handleMoveTo = (skill: string, sourceTier: 'required' | 'mandatory' | 'desirable', targetTierKey: string) => {
    let r = [...search.requiredSkills];
    let m = [...search.mandatorySkills];
    let d = [...search.desirableSkills];

    if (sourceTier === 'required') r = r.filter(s => s !== skill);
    if (sourceTier === 'mandatory') m = m.filter(s => s !== skill);
    if (sourceTier === 'desirable') d = d.filter(s => s !== skill);

    if (targetTierKey === 'required' && !r.includes(skill)) r.push(skill);
    if (targetTierKey === 'mandatory' && !m.includes(skill)) m.push(skill);
    if (targetTierKey === 'desirable' && !d.includes(skill)) d.push(skill);

    onPatchSkills(r, m, d);
  };

  const coverage = pool?.coverage || {};
  const skillMatches = search.skillMatches || [];
  const totalSkillsCount = (search.requiredSkills?.length || 0) + (search.mandatorySkills?.length || 0) + (search.desirableSkills?.length || 0);

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'grey.200',
        overflow: 'hidden',
      }}
    >
      {/* Top compact header bar */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 200 }}>
          <TextField
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            placeholder="Search Title"
            variant="outlined"
            sx={{
              maxWidth: 360,
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                fontWeight: 700,
                py: 0.5,
                px: 1,
              },
            }}
          />
          {isReindexing && (
            <Chip
              label="Re-indexing"
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 700,
                fontSize: '0.625rem',
                textTransform: 'uppercase',
                height: 22,
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {search.source === 'opening' && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<RotateCw size={13} />}
              onClick={() => {
                if (window.confirm('This discards your edits. Continue?')) {
                  onRefreshFromOpening();
                }
              }}
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                borderColor: 'grey.300',
                bgcolor: 'background.paper',
              }}
            >
              Refresh
            </Button>
          )}

          {/* Criteria Collapse / Expand Button */}
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setIsCriteriaExpanded(!isCriteriaExpanded)}
            endIcon={isCriteriaExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'none',
              borderColor: 'grey.300',
              bgcolor: 'background.paper',
              color: 'text.secondary',
            }}
          >
            {isCriteriaExpanded ? 'Collapse Criteria' : `Criteria (${totalSkillsCount} skills)`}
          </Button>
        </Stack>
      </Box>

      {/* Collapsible Search Criteria Section */}
      {isCriteriaExpanded && (
        <Box sx={{ p: 2 }}>
          {/* Quick inline controls: Target Job Title & Experience on single strip */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              pb: 2,
              borderBottom: '1px solid',
              borderColor: 'grey.100',
              alignItems: 'center',
            }}
          >
            {/* Target Job Title */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                Role Title:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                onBlur={handleJobTitleBlur}
                placeholder="e.g. Senior Software Engineer"
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '0.75rem',
                    py: 0.75,
                    px: 1.25,
                  },
                }}
              />
            </Stack>

            {/* Experience Controls */}
            <Stack direction="row" alignItems="center" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Min Exp:
                </Typography>
                <TextField
                  type="number"
                  size="small"
                  inputProps={{ min: 0 }}
                  value={search.minimumWorkExperience || 0}
                  onChange={(e) => onPatchExperience(parseInt(e.target.value) || 0, search.requireExperience)}
                  sx={{
                    width: 72,
                    '& .MuiInputBase-input': {
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      py: 0.75,
                      textAlign: 'center',
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  yrs
                </Typography>
              </Stack>

              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={search.requireExperience}
                    onChange={(e) => onPatchExperience(search.minimumWorkExperience || 0, e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Strictly Require
                  </Typography>
                }
                sx={{
                  m: 0,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1.5,
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              />
            </Stack>
          </Box>

          {/* Skill Tier Rows */}
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <SkillChipRow
              label="Must Have"
              emptyHint="Empty by default — add a skill here to exclude candidates who lack it."
              badgeBg="#fef2f2"
              badgeText="#b91c1c"
              skills={search.requiredSkills}
              skillMatches={skillMatches}
              coverage={coverage}
              tiers={[
                { label: 'Important', key: 'mandatory' },
                { label: 'Nice to Have', key: 'desirable' },
              ]}
              onSkillsChange={(s) => handleSkillsChange('required', s)}
              onMoveTo={(skill, target) => handleMoveTo(skill, 'required', target)}
              onRemoveMatch={(skill, match) => onPatchRemoveMatches(skill, [match])}
              isReindexing={isReindexing}
            />
            
            <SkillChipRow
              label="Important"
              badgeBg="#fffbeb"
              badgeText="#b45309"
              skills={search.mandatorySkills}
              skillMatches={skillMatches}
              coverage={coverage}
              tiers={[
                { label: 'Must Have', key: 'required' },
                { label: 'Nice to Have', key: 'desirable' },
              ]}
              onSkillsChange={(s) => handleSkillsChange('mandatory', s)}
              onMoveTo={(skill, target) => handleMoveTo(skill, 'mandatory', target)}
              onRemoveMatch={(skill, match) => onPatchRemoveMatches(skill, [match])}
              isReindexing={isReindexing}
            />
            
            <SkillChipRow
              label="Nice to Have"
              badgeBg="#eff6ff"
              badgeText="#1d4ed8"
              skills={search.desirableSkills}
              skillMatches={skillMatches}
              coverage={coverage}
              tiers={[
                { label: 'Must Have', key: 'required' },
                { label: 'Important', key: 'mandatory' },
              ]}
              onSkillsChange={(s) => handleSkillsChange('desirable', s)}
              onMoveTo={(skill, target) => handleMoveTo(skill, 'desirable', target)}
              onRemoveMatch={(skill, match) => onPatchRemoveMatches(skill, [match])}
              isReindexing={isReindexing}
            />
          </Stack>

          {/* Advanced Weights Disclosure */}
          <Box sx={{ pt: 1.5, mt: 1.5, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <Button
              size="small"
              onClick={() => setAdvancedOpen(!advancedOpen)}
              endIcon={advancedOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              sx={{
                p: 0,
                fontSize: '0.75rem',
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'none',
                '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
              }}
            >
              {advancedOpen ? 'Hide Scoring Weights' : 'Adjust Scoring Weights'}
            </Button>
            
            {advancedOpen && (
              <Box
                sx={{
                  mt: 1.5,
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <WeightSliders
                  weights={search.weights}
                  override={weightOverride}
                  onOverrideChange={(newOverride) => {
                    onWeightOverrideChange(newOverride);
                    onApplyWeightOverride({
                      weights: {
                        semantic: newOverride.semantic,
                        mandatory_skills: newOverride.mandatorySkills,
                        desirable_skills: newOverride.desirableSkills,
                        experience: newOverride.experience,
                      },
                    });
                  }}
                  onCommit={onCommitWeights}
                  onReset={onResetWeights}
                />
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};
